/**
 * CRM route subtree. Mount inside the existing router:
 *
 *   import { crmRoutes } from '@/crm/routes'
 *   const router = createBrowserRouter([
 *     { path: '/', element: <AppShell />, children: [ ...existing, ...crmRoutes ] },
 *   ])
 *
 * The CRM keeps the host app's shell, sidebar and tokens — only the inner layout
 * is ours. Add the CRM's entries to the existing nav rather than rendering a
 * second sidebar; `crmNavigation` below is that list.
 */

import { Outlet, useLoaderData, type RouteObject } from 'react-router-dom'
import type { Lookups } from '@erp/contracts'
import { api } from './api'
import type { ExtensibleLookup } from '@erp/contracts'
import type { ActionFunctionArgs } from 'react-router-dom'
import { LookupProvider } from './status-context'

import Leads, {
  loader as leadsLoader,
  pageLoader as leadsPage,
  action as leadsAction,
} from './routes/leads'
import LeadDetail, { loader as leadLoader, action as leadAction } from './routes/lead-detail'
import NewLead, { action as newLeadAction } from './routes/lead-new'
import EditLead, { loader as editLeadLoader, action as editLeadAction } from './routes/lead-edit'
import DealsBoard, {
  loader as boardLoader,
  action as boardAction,
} from './routes/deals-board'
import DealsList, {
  loader as dealsListLoader,
  pageLoader as dealsListPage,
  action as dealsListAction,
} from './routes/deals-list'
import DealDetail, { loader as dealLoader, action as dealAction } from './routes/deal-detail'
import NewDeal, { loader as newDealLoader, action as newDealAction } from './routes/deal-new'
import EditDeal, { loader as editDealLoader, action as editDealAction } from './routes/deal-edit'
import Organizations, {
  loader as organizationsLoader,
  pageLoader as organizationsPage,
  action as organizationsAction,
} from './routes/organizations'
import OrganizationDetail, {
  loader as organizationLoader,
  action as organizationAction,
} from './routes/organization-detail'
import NewOrganization, { action as newOrganizationAction } from './routes/organization-new'
import EditOrganization, {
  loader as editOrganizationLoader,
  action as editOrganizationAction,
} from './routes/organization-edit'
import Contacts, {
  loader as contactsLoader,
  pageLoader as contactsPage,
  action as contactsAction,
} from './routes/contacts'
import ContactDetail, {
  loader as contactLoader,
  action as contactAction,
} from './routes/contact-detail'
import NewContact, {
  loader as newContactLoader,
  action as newContactAction,
} from './routes/contact-new'
import EditContact, {
  loader as editContactLoader,
  action as editContactAction,
} from './routes/contact-edit'
import Tasks, {
  loader as tasksLoader,
  pageLoader as tasksPage,
  action as tasksAction,
} from './routes/tasks'

/** Sidebar entries for the host app's existing navigation. */
export const crmNavigation = [
  { to: '/crm/leads', label: 'Leads' },
  { to: '/crm/deals', label: 'Pipeline' },
  { to: '/crm/organizations', label: 'Organizations' },
  { to: '/crm/contacts', label: 'Contacts' },
  { to: '/crm/tasks', label: 'Tasks' },
] as const

/**
 * Every lookup in one request, loaded once for the whole subtree. Screens below
 * read them from context rather than joining them onto rows.
 */
const layoutLoader = (): Promise<Lookups> => api.lookups()

/**
 * Action-only route. LookupSelect posts here rather than to whichever form it
 * happens to sit inside, so entity actions stay unaware of it. Completing the
 * action revalidates every active loader, including the layout's — which is how
 * the new value reaches the pickers.
 */
export async function lookupAction({ request }: ActionFunctionArgs) {
  const form = await request.formData()
  if (String(form.get('intent')) !== 'lookup.create') {
    return { ok: false, message: 'Unknown action' }
  }
  return api.createLookup({
    kind: String(form.get('kind')) as ExtensibleLookup,
    name: String(form.get('name') ?? ''),
  })
}

function CrmLayout() {
  const lookups = useLoaderData() as Lookups
  return (
    <LookupProvider value={lookups}>
      <div className="ax-crm">
        <Outlet />
      </div>
    </LookupProvider>
  )
}

/**
 * The `page` routes render nothing — they exist so `fetcher.load()` can read the
 * next keyset page without navigating. Static segments outrank dynamic ones in
 * react-router's ranking, so `leads/new` and `leads/page` are never mistaken for
 * `leads/:leadId`.
 */
export const crmRoutes: RouteObject[] = [
  {
    path: 'crm',
    element: <CrmLayout />,
    loader: layoutLoader,
    children: [
      { index: true, loader: () => redirectTo('/crm/leads') },
      { path: 'lookups', action: lookupAction },

      { path: 'leads', element: <Leads />, loader: leadsLoader, action: leadsAction },
      { path: 'leads/page', loader: leadsPage },
      { path: 'leads/new', element: <NewLead />, action: newLeadAction },
      { path: 'leads/:leadId', element: <LeadDetail />, loader: leadLoader, action: leadAction },
      {
        path: 'leads/:leadId/edit',
        element: <EditLead />,
        loader: editLeadLoader,
        action: editLeadAction,
      },

      { path: 'deals', element: <DealsBoard />, loader: boardLoader, action: boardAction },
      {
        path: 'deals/list',
        element: <DealsList />,
        loader: dealsListLoader,
        action: dealsListAction,
      },
      { path: 'deals/list/page', loader: dealsListPage },
      { path: 'deals/new', element: <NewDeal />, loader: newDealLoader, action: newDealAction },
      { path: 'deals/:dealId', element: <DealDetail />, loader: dealLoader, action: dealAction },
      {
        path: 'deals/:dealId/edit',
        element: <EditDeal />,
        loader: editDealLoader,
        action: editDealAction,
      },

      {
        path: 'organizations',
        element: <Organizations />,
        loader: organizationsLoader,
        action: organizationsAction,
      },
      { path: 'organizations/page', loader: organizationsPage },
      {
        path: 'organizations/new',
        element: <NewOrganization />,
        action: newOrganizationAction,
      },
      {
        path: 'organizations/:organizationId',
        element: <OrganizationDetail />,
        loader: organizationLoader,
        action: organizationAction,
      },
      {
        path: 'organizations/:organizationId/edit',
        element: <EditOrganization />,
        loader: editOrganizationLoader,
        action: editOrganizationAction,
      },

      { path: 'contacts', element: <Contacts />, loader: contactsLoader, action: contactsAction },
      { path: 'contacts/page', loader: contactsPage },
      {
        path: 'contacts/new',
        element: <NewContact />,
        loader: newContactLoader,
        action: newContactAction,
      },
      {
        path: 'contacts/:contactId',
        element: <ContactDetail />,
        loader: contactLoader,
        action: contactAction,
      },
      {
        path: 'contacts/:contactId/edit',
        element: <EditContact />,
        loader: editContactLoader,
        action: editContactAction,
      },

      { path: 'tasks', element: <Tasks />, loader: tasksLoader, action: tasksAction },
      { path: 'tasks/page', loader: tasksPage },
    ],
  },
]

function redirectTo(path: string): Response {
  return new Response(null, { status: 302, headers: { Location: path } })
}
